import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import {
    getAllCounties,
    getCompetenceAreas,
    getMunicipalitiesByCounty,
} from 'Frontend/scripts/services/profile.service'
import { ICompetenceArea, ICounty, IMunicipality } from 'Frontend/scripts/models/profile.model'
import { ISelectOption } from 'Frontend/scripts/models/forums.model'
import MultiSelectComponent from 'Features/MultiSelectComponent/MultiSelectComponent'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import {
    checkIfPasswordIsValid,
    checkIfUsernameIsAvailable,
    checkIfUsernameIsValid,
    createMemberApplication,
} from 'Frontend/scripts/services/membershipApplication.service'
import { IRegisterUserForm } from 'Frontend/scripts/models/user.model'
import { useTranslation } from 'react-i18next'
import Dialog from 'Features/Dialog/Dialog'

interface RegisterUserProps {
    membershipTermsLink: string
}
const RegisterUser: FunctionComponent<RegisterUserProps> = ({ membershipTermsLink }) => {
    const [counties, setCounties] = useState<ISelectOption[]>([])
    const [municipalities, setMunicipalities] = useState<ISelectOption[]>([])
    const [competences, setCompetences] = useState<ISelectOption[]>([])
    const [applicationFailed, setApplicationFailed] = useState<boolean>(false)
    const { t } = useTranslation()
    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        setError,
        formState: { errors },
    } = useForm<IRegisterUserForm>({
        defaultValues: {
            firstname: '',
            lastname: '',
            username: '',
            phone: '',
            mobilePhone: '',
            postalCode: '',
            authority: '',
            title: '',
            county: {},
            municipality: [],
            competence: [],
            password: '',
            confirmPassword: '',
            subscribeToNewsletter: false,
            acceptMembershipTerms: false,
        },
    })

    const password = watch('password')
    const emailRef = useRef<HTMLDivElement | null>(null)

    const onSubmit = async (data: IRegisterUserForm) => {
        const token = await getAntiforgeryToken()
        if (token) {
            const isUsernameValid = await checkIfUsernameIsValid(token.csrfToken, data.username)
            if (isUsernameValid) {
                const isUsernameAvailble = await checkIfUsernameIsAvailable(token.csrfToken, data.username)
                if (isUsernameAvailble) {
                    const isPasswordValid = await checkIfPasswordIsValid(token.csrfToken, data.password)
                    if (isPasswordValid) {
                        createMemberApplication(token.csrfToken, data).then((responseObj) => {
                            {
                                if (responseObj.status == 200 && responseObj.redirectLink) {
                                    window.location.href = responseObj.redirectLink
                                } else {
                                    setApplicationFailed(true)
                                }
                            }
                        })
                    } else {
                        setError('password', {
                            type: 'manual',
                            message: t('register-user.error-password-wrong-format'),
                        })
                    }
                } else {
                    setError('username', {
                        type: 'manual',
                        message: t('register-user.error-email-not-available'),
                    })
                    emailRef?.current && emailRef.current.scrollIntoView()
                }
            } else {
                setError('username', {
                    type: 'manual',
                    message: t('register-user.error-wrong-email'),
                })
                emailRef?.current && emailRef.current.scrollIntoView()
            }
        }
    }

    const updateMunicipalities = (countyId: string) => {
        getMunicipalities(countyId)
        setValue('municipality', [], { shouldValidate: true })
    }

    const getMunicipalities = (countyId: string) => {
        getMunicipalitiesByCounty(countyId).then((municipalitiesData: IMunicipality[]) => {
            const convertedMunicipalityArray = municipalitiesData?.map((municipality) => ({
                value: municipality.id.toString(),
                label: municipality.name,
            }))
            setMunicipalities(convertedMunicipalityArray)
        })
    }

    useEffect(() => {
        getAllCounties().then((countiesData: ICounty[]) => {
            const convertedCountiesArray = countiesData.map((county) => ({
                value: county.id.toString(),
                label: county.name,
            }))
            setCounties(convertedCountiesArray)
        })

        getCompetenceAreas().then((competences: ICompetenceArea[]) => {
            const convertedCompetencesArray = competences.map((competence) => ({
                value: competence.id.toString(),
                label: competence.name,
            }))
            setCompetences(convertedCompetencesArray)
        })
    }, [])

    useEffect(() => {
        if (counties.length > 0 && getValues('county.value') != undefined) {
            getMunicipalities(getValues('county.value'))
        }
    }, [counties])
    return (
        <>
            <p className="profile-page-edit__mandatory-field-explanation">{t('mandatory-field-explanation')}</p>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Form.Group>
                    <Form.Label htmlFor="inputFirstname">{t('firstname')}*</Form.Label>
                    <Form.Control
                        id="inputFirstname"
                        type="text"
                        {...register('firstname', {
                            required: t('error-add-firstname'),
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.firstname}
                    />
                    <Form.Control.Feedback type="invalid">{errors.firstname?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="inputLastname">{t('lastname')}*</Form.Label>
                    <Form.Control
                        id="inputLastname"
                        type="text"
                        {...register('lastname', {
                            required: t('error-add-lastname'),
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.lastname}
                    />
                    <Form.Control.Feedback type="invalid">{errors.lastname?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group ref={emailRef}>
                    <Form.Label htmlFor="inputEmail">{t('official-email')}*</Form.Label>
                    <Form.Control
                        id="inputEmail"
                        type="text"
                        {...register('username', {
                            required: t('error-add-email'),
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.username}
                    />
                    <Form.Control.Feedback type="invalid">{errors.username?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="inputPhone">{t('phone')}*</Form.Label>
                    <Form.Control
                        id="inputPhone"
                        type="text"
                        {...register('phone', {
                            required: t('error-add-phone'),
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">{errors.phone?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="inputMobile">{t('mobile-phone')}</Form.Label>
                    <Form.Control
                        id="inputMobile"
                        type="text"
                        {...register('mobilePhone', {
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.mobilePhone}
                    />
                    <Form.Control.Feedback type="invalid">{errors.mobilePhone?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="inputPostalCode">{t('postal-code')}</Form.Label>
                    <Form.Control
                        id="inputPostalCode"
                        type="text"
                        {...register('postalCode', {
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.postalCode}
                    />
                    <Form.Control.Feedback type="invalid">{errors.postalCode?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="inputAuthority">{t('authority')}*</Form.Label>
                    <Form.Control
                        id="inputAuthority"
                        type="text"
                        {...register('authority', {
                            required: t('error-add-authority'),
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.authority}
                    />
                    <Form.Control.Feedback type="invalid">{errors.authority?.message}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                    <Form.Label htmlFor="inputTitle">{t('title')}</Form.Label>
                    <Form.Control
                        id="inputTitle"
                        type="text"
                        {...register('title', {
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.title}
                    />
                    <Form.Control.Feedback type="invalid">{errors.title?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="county">{t('county')}</Form.Label>
                    <Controller
                        name="county"
                        control={control}
                        render={({ field }) => (
                            <Select
                                inputId="county"
                                value={field.value}
                                options={counties}
                                placeholder={t('error-choose-county')}
                                classNamePrefix="single-select"
                                onChange={(value) => {
                                    field.onChange(value)
                                    value && updateMunicipalities(value.value.toString())
                                }}
                                styles={{
                                    control: (baseStyles) => ({
                                        ...baseStyles,
                                        fontSize: '1.6rem',
                                    }),
                                    option: (baseStyles) => ({
                                        ...baseStyles,
                                        fontSize: '1.6rem',
                                    }),
                                }}
                            />
                        )}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="municipality">{t('municipality')}</Form.Label>
                    <Controller
                        name="municipality"
                        control={control}
                        render={({ field }) => (
                            <MultiSelectComponent
                                inputId="municipality"
                                key={JSON.stringify(field.value)}
                                options={municipalities}
                                addToUrl={false}
                                placeholderText={t('choose-municipality')}
                                value={field.value || []}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label htmlFor="competence">{t('competence-areas')}</Form.Label>
                    <Controller
                        name="competence"
                        control={control}
                        render={({ field }) => (
                            <MultiSelectComponent
                                inputId="competence"
                                options={competences}
                                addToUrl={false}
                                placeholderText={t('choose-competence')}
                                value={field.value || []}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label htmlFor="inputPassword">{t('register-user.password')}*</Form.Label>
                    <Form.Control
                        id="inputPassword"
                        type="password"
                        {...register('password', {
                            required: t('register-user.error-add-password'),
                            minLength: {
                                value: 6,
                                message: t('register-user.error-password-to-short'),
                            },
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                        })}
                        isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">{errors.password?.message}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                    <Form.Label htmlFor="inputConfirmPassword">{t('register-user.confirm-password')}*</Form.Label>
                    <Form.Control
                        id="inputConfirmPassword"
                        type="password"
                        {...register('confirmPassword', {
                            required: t('register-user.error-add-confirm-password'),
                            minLength: {
                                value: 6,
                                message: t('register-user.error-password-to-short'),
                            },
                            maxLength: {
                                value: 255,
                                message: t('error-text-to-long'),
                            },
                            validate: (value) => value === password || t('register-user.error-password-missmatch'),
                        })}
                        isInvalid={!!errors.confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">{errors.confirmPassword?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                    <Form.Check
                        type="checkbox"
                        id="subscribe"
                        className="mt-3"
                        label={
                            <>
                                {t('subscribe-to-newsletter')}{' '}
                            </>
                        }
                        {...register('subscribeToNewsletter')}
                    />
                    <Form.Check
                        type="checkbox"
                        id="email-reminders"
                        className="mt-3"
                        label={
                            <>
                                {t('read-and-approve')}{' '}
                                <a href={membershipTermsLink} className="membership-terms-link">
                                    {t('terms-for-membership')}
                                </a>
                            </>
                        }
                        {...register('acceptMembershipTerms', {
                            required: t('register-user.error-confirmation-membership-terms'),
                        })}
                        isInvalid={!!errors.acceptMembershipTerms}
                    />
                    <Form.Control.Feedback type="invalid">{errors.acceptMembershipTerms?.message}</Form.Control.Feedback>

                    {errors.acceptMembershipTerms && (
                        <Form.Text className="text-danger">{errors.acceptMembershipTerms.message}</Form.Text>
                    )}
                </Form.Group>
                <Button variant="secondary" type="submit" className="mt-4">
                    {t('register-user.apply-for-membership')}
                </Button>
            </Form>
            {applicationFailed && (
                <Dialog
                    callbackClose={() => setApplicationFailed(false)}
                    callbackSubmit={() => setApplicationFailed(false)}
                    isOpen={applicationFailed}
                    buttonVariant="secondary"
                    submitText={t('register-user.ok')}
                    verificationText={`${t('register-user.something-went-wrong')}`}
                ></Dialog>
            )}
        </>
    )
}
export default RegisterUser
